/**
 * <gx-menu-drawer>
 *
 * Standalone mobile menu drawer for Mécafé. Deliberately independent from
 * Dawn's <header-drawer>/<menu-drawer> (global.js): that element wires a
 * click listener on every <button> inside it to close whichever submenu it
 * is in, and its accordion is a drill-down (each level replaces the
 * previous one full-screen). Here submenus open in place, one at a time,
 * so reusing that class would fight this file on every toggle.
 *
 * Relies on `trapFocus` / `removeTrapFocus`, defined as global functions in
 * global.js, which is loaded earlier in layout/theme.liquid.
 */
class GxMenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.trigger = this.querySelector('.gx-drawer-trigger');
    this.overlay = this.querySelector('.gx-drawer-overlay');
    this.panel = this.querySelector('.gx-drawer-panel');
    this.closeButton = this.querySelector('.gx-drawer-close');
    this.toggles = Array.from(this.querySelectorAll('.gx-drawer-item__toggle'));

    this.headerRow = this.querySelector('.gx-drawer-header__row');
    this.searchToggle = this.querySelector('.gx-drawer-search-toggle');
    this.searchForm = this.querySelector('.gx-drawer-search-form');
    this.searchCancel = this.querySelector('.gx-drawer-search-cancel');
    this.searchInput = this.querySelector('.gx-drawer-search-input');

    if (!this.trigger || !this.overlay || !this.panel) return;

    this.trigger.addEventListener('click', this.open.bind(this));
    this.closeButton?.addEventListener('click', this.close.bind(this));
    this.overlay.addEventListener('click', this.onOverlayClick.bind(this));
    this.addEventListener('keydown', this.onKeyDown.bind(this));

    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', this.onToggleClick.bind(this));
    });

    this.searchToggle?.addEventListener('click', this.openSearch.bind(this));
    this.searchCancel?.addEventListener('click', this.closeSearch.bind(this));
  }

  open() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

    this.overlay.hidden = false;
    // Force layout so the transition plays instead of jumping straight to open.
    this.overlay.offsetHeight;
    this.overlay.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden-mobile');

    trapFocus(this.panel, this.closeButton || this.panel);

    window.addEventListener('resize', this.onResize);
  }

  close() {
    if (!this.overlay.classList.contains('is-open')) return;

    this.overlay.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden-mobile');
    removeTrapFocus(this.trigger);
    window.removeEventListener('resize', this.onResize);

    // Collapse any open accordion section and wait for the closing
    // transition before hiding, so the drawer doesn't reopen mid-animation.
    this.toggles.forEach((toggle) => this.collapseSection(toggle));
    this.closeSearch();

    const onTransitionEnd = (event) => {
      if (event.target !== this.overlay) return;
      this.overlay.hidden = true;
      this.overlay.removeEventListener('transitionend', onTransitionEnd);
    };
    this.overlay.addEventListener('transitionend', onTransitionEnd);
  }

  onOverlayClick(event) {
    if (event.target === this.overlay) this.close();
  }

  onKeyDown(event) {
    if (event.code?.toUpperCase() !== 'ESCAPE') return;

    // ESC backs out of the search bar first, then closes the whole drawer.
    if (this.searchForm && !this.searchForm.hidden) {
      this.closeSearch();
      return;
    }

    this.close();
  }

  openSearch() {
    if (!this.headerRow || !this.searchForm) return;

    this.headerRow.hidden = true;
    this.searchForm.hidden = false;
    this.searchToggle.setAttribute('aria-expanded', 'true');
    this.searchInput?.focus();
  }

  closeSearch() {
    if (!this.headerRow || !this.searchForm || this.searchForm.hidden) return;

    this.searchForm.hidden = true;
    this.headerRow.hidden = false;
    this.searchToggle?.setAttribute('aria-expanded', 'false');
    if (this.searchInput) this.searchInput.value = '';
  }

  onResize = () => {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };

  onToggleClick(event) {
    const toggle = event.currentTarget;
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';

    // Accordion: only one section open at a time, so opening one collapses
    // whichever other section was open.
    this.toggles.forEach((otherToggle) => {
      if (otherToggle !== toggle) this.collapseSection(otherToggle);
    });

    isOpen ? this.collapseSection(toggle) : this.expandSection(toggle);
  }

  expandSection(toggle) {
    const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!submenu) return;

    toggle.setAttribute('aria-expanded', 'true');
    submenu.hidden = false;
  }

  collapseSection(toggle) {
    const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!submenu) return;

    toggle.setAttribute('aria-expanded', 'false');
    submenu.hidden = true;
  }
}

customElements.define('gx-menu-drawer', GxMenuDrawer);
